<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
// import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faChartLine,
  faUsers,
  faClipboardList,
  faBoxes,
  faBell,
  faCog,
  faCalendarAlt,
  faTruck,
} from '@fortawesome/free-solid-svg-icons'
const authStore = useAuthStore()
const showProfileMenu = ref(false)
const profileMenuRef = ref<HTMLElement | null>(null)
// const notifications = ref(3)

library.add(faChartLine, faUsers, faClipboardList, faBoxes, faBell, faCog, faCalendarAlt, faTruck)

// Compute user initials from first and last name
const userInitials = computed(() => {
  if (!authStore.user?.first_name || !authStore.user?.last_name) return 'U'
  return `${authStore.user.first_name.charAt(0)}${authStore.user.last_name.charAt(0)}`.toUpperCase()
})

// Compute full name
const fullName = computed(() => {
  if (!authStore.user?.first_name || !authStore.user?.last_name) return 'User'
  return `${authStore.user.first_name} ${authStore.user.last_name}`
})

import { useRouter } from 'vue-router'
const router = useRouter()

const logout = async () => {
  await authStore.logout()
  router.push('/login')
}

// Click outside handler
const handleClickOutside = (event: MouseEvent) => {
  if (profileMenuRef.value && !profileMenuRef.value.contains(event.target as Node)) {
    showProfileMenu.value = false
  }
}

// Add event listener when component is mounted
onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
})

// Remove event listener when component is unmounted
onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside)
})
</script>

<template>
  <div class="bg-white rounded-lg shadow-md p-6 mb-6 border border-gray-100">
    <div class="flex flex-col md:flex-row md:items-center md:justify-between">
      <div>
        <h2 class="text-2xl font-bold text-gray-900">
          Welcome back, {{ authStore.user?.first_name }}!
        </h2>
        <p class="mt-1 text-gray-600">Here's what's happening with your operations today.</p>
      </div>

      <div
        class="flex flex-col md:flex-row md:justify-between md:items-center gap-4 p-4 bg-white rounded-lg shadow-sm border border-gray-100"
      >
        <!-- Profile Menu -->
        <div
          class="relative hover:border-2 rounded-lg hover:border-blue-400 self-start"
          ref="profileMenuRef"
        >
          <button
            @click="showProfileMenu = !showProfileMenu"
            class="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full md:w-auto"
            aria-haspopup="true"
            :aria-expanded="showProfileMenu"
          >
            <div class="relative">
              <div
                class="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm shadow-md"
              >
                {{ userInitials }}
              </div>
              <div
                class="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"
              ></div>
            </div>
            <div class="text-left">
              <p class="text-sm font-medium text-gray-900">{{ fullName }}</p>
              <p class="text-xs text-gray-500">@{{ authStore.user?.username }}</p>
            </div>
            <svg
              class="w-4 h-4 text-gray-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              :class="{ 'transform rotate-180': showProfileMenu }"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              ></path>
            </svg>
          </button>

          <!-- Profile Dropdown Menu -->
          <div
            v-if="showProfileMenu"
            class="absolute left-0 md:left-auto md:right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200"
          >
            <div class="px-4 py-2 border-b border-gray-100 md:hidden">
              <p class="text-sm font-medium text-gray-900">{{ fullName }}</p>
              <p class="text-xs text-gray-500">@{{ authStore.user?.username }}</p>
            </div>
            <RouterLink
              to="/profile"
              class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <svg
                class="w-4 h-4 mr-2 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                ></path>
              </svg>
              Your Profile
            </RouterLink>
            <RouterLink
              to="/profile"
              class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <svg
                class="w-4 h-4 mr-2 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                ></path>
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                ></path>
              </svg>
              Settings
            </RouterLink>
            <a
              href="/contact"
              class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <svg
                class="w-4 h-4 mr-2 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                ></path>
              </svg>
              Support
            </a>
            <div class="border-t border-gray-100"></div>
            <button
              @click="logout"
              class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center md:hidden"
            >
              <svg
                class="w-4 h-4 mr-2 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                ></path>
              </svg>
              Sign out
            </button>
          </div>
        </div>

        <!-- Logout Button - Visible on medium screens and up -->
        <div class="hidden md:flex items-center justify-center">
          <button
            @click="logout"
            class="bg-red-700 text-white px-4 py-2 rounded-lg hover:bg-red-600 flex items-center transition-colors w-full md:w-auto justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Log Out
          </button>
        </div>

        <!-- Logout Button - Visible on small screens (inside dropdown) -->
        <!-- Already implemented in the dropdown menu with md:hidden class -->
      </div>
    </div>
  </div>
</template>

<style scoped>
.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.2s ease-in-out;
}
</style>
